// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — a region through time: its mean, highest and
 * lowest value in every frame (docs/AYNI_XR_WHEN_EXTREMES_PLAN.md, step 2).
 *
 * The question it answers: *when* was the highest (or lowest) value in a
 * box, a latitude band, a country or the whole globe, and where in it. The
 * Statistics tab measures one frame; this keeps one result per frame of the
 * dataset's time axis as playback passes the frames, the way the area over
 * a threshold does (`threshold.ts`, whose frame series it shares), and
 * `measure/whenExtremes.ts` names the frames holding the record high, the
 * record low and the highest and lowest mean.
 *
 * Per frame, over the region's cells (the dataset's own grid, no
 * regridding; a country's shape inside its box, `datasetStats.TexelWindow`):
 *
 * - **The mean is area-weighted** by each cell's exact area on the sphere
 *   (`threshold.cellRowAreasKm2`), as the region statistics are: an
 *   unweighted mean of an equirectangular grid counts a polar cell as much
 *   as an equatorial one, which it is not.
 * - **The highest and lowest are cell values**, decoded by the dataset's
 *   own rules (`changeMap.valueTable`: log and stepped scales included);
 *   no-data cells are left out and counted.
 * - **Where**: the first cell in the grid's reading order holding the value
 *   (north-west first on an ordinary grid), with the number of cells that
 *   share it. 8-bit data that clips at the top of its scale holds its
 *   highest value in many cells; the count says so rather than one cell
 *   standing for the patch.
 *
 * Classes (a classified release) have no mean or highest: the panel says so
 * and records nothing.
 *
 * Pure: no DOM.
 */

import { texelUvToLatLon } from '../datasetProbe'
import { inWindowMask, type TexelWindow } from '../datasetStats'
import type { DatasetOverlayOptions } from '../../types'
import { createSeries, type MeasurementSeries } from '../measure/series'
import { extremesOf } from '../measure/whenExtremes'
import { AUTHALIC_RADIUS_KM, MAX_CHART_SLOTS, createFrameSeries, frameStampMs, skippedThresholdFrames, type FrameSeries } from './threshold'
import type { ClimateAxis } from './climatology'

/** One frame as the measurement reads it. */
export interface RegionFrame {
  readonly codes: Uint8Array
  readonly width: number
  readonly height: number
  /** Each code's value, NaN for no data (`changeMap.valueTable`). */
  readonly values: ArrayLike<number>
  /** `threshold.cellRowAreasKm2` for this grid. */
  readonly rowAreas: Float64Array
  /** The region's cells; undefined for the whole frame. */
  readonly window?: TexelWindow
  /** The dataset's placement, to name where a cell is. */
  readonly options?: DatasetOverlayOptions
}

export interface RegionFrameResult {
  /** Area-weighted mean of the cells with data; null without any. */
  readonly mean: number | null
  readonly max: number | null
  readonly min: number | null
  /** The first cell (reading order) holding the highest / lowest value. */
  readonly maxAt: { lat: number; lon: number } | null
  readonly minAt: { lat: number; lon: number } | null
  /** Cells holding exactly the highest / lowest value. */
  readonly maxCells: number
  readonly minCells: number
  readonly cellsWithData: number
  readonly cellsExamined: number
  /** Area of the cells with data, and of every cell of the region, km². */
  readonly areaWithDataKm2: number
  readonly regionAreaKm2: number
}

/** Measure one frame over the region: its mean, highest and lowest, and where. */
export function measureRegion(frame: RegionFrame): RegionFrameResult {
  const { codes, width, height, values, rowAreas } = frame
  const win = frame.window ?? { x0: 0, y0: 0, x1: width, y1: height }
  const x0 = Math.max(0, Math.floor(win.x0))
  const y0 = Math.max(0, Math.floor(win.y0))
  const x1 = Math.min(width, Math.ceil(win.x1))
  const y1 = Math.min(height, Math.ceil(win.y1))
  const masked = frame.window?.mask ? frame.window : null
  // One pass by code: its area, its cells and its first texel; the values are read per code, not per cell.
  const area = new Float64Array(256)
  const cells = new Uint32Array(256)
  const first = new Int32Array(256).fill(-1)
  let examined = 0
  let region = 0
  for (let y = y0; y < y1; y++) {
    const a = rowAreas[y] ?? 0
    const row = y * width
    for (let x = x0; x < x1; x++) {
      if (masked && !inWindowMask(masked, x, y)) continue
      examined++
      region += a
      const c = codes[row + x]
      area[c] += a
      if (cells[c]++ === 0) first[c] = row + x
    }
  }
  let sum = 0
  let weight = 0
  let withData = 0
  let max = -Infinity
  let min = Infinity
  for (let c = 0; c < 256; c++) {
    if (!cells[c]) continue
    const v = values[c]
    if (!Number.isFinite(v)) continue
    withData += cells[c]
    sum += area[c] * v
    weight += area[c]
    if (v > max) max = v
    if (v < min) min = v
  }
  if (!withData) {
    return { mean: null, max: null, min: null, maxAt: null, minAt: null, maxCells: 0, minCells: 0, cellsWithData: 0, cellsExamined: examined, areaWithDataKm2: 0, regionAreaKm2: region }
  }
  // Several codes can decode to one value (a stepped scale): every one of them holds it.
  let maxCells = 0
  let minCells = 0
  let maxFirst = Infinity
  let minFirst = Infinity
  for (let c = 0; c < 256; c++) {
    if (!cells[c]) continue
    const v = values[c]
    if (v === max) {
      maxCells += cells[c]
      maxFirst = Math.min(maxFirst, first[c])
    }
    if (v === min) {
      minCells += cells[c]
      minFirst = Math.min(minFirst, first[c])
    }
  }
  const where = (i: number): { lat: number; lon: number } => {
    const x = i % width
    const y = (i - x) / width
    return texelUvToLatLon({ u: (x + 0.5) / width, v: (y + 0.5) / height }, frame.options)
  }
  return {
    mean: weight > 0 ? sum / weight : null,
    max,
    min,
    maxAt: where(maxFirst),
    minAt: where(minFirst),
    maxCells,
    minCells,
    cellsWithData: withData,
    cellsExamined: examined,
    areaWithDataKm2: weight,
    regionAreaKm2: region,
  }
}

// ---------------------------------------------------------------- the series

export type RegionSeries = FrameSeries<RegionFrameResult>

/** An empty series over the axis's frames (`threshold.createFrameSeries`). */
export function createRegionSeries(axis: ClimateAxis, usable?: (frame: number) => boolean): RegionSeries | null {
  return createFrameSeries<RegionFrameResult>(axis, usable)
}

/** Which statistic of each frame a chart or an extreme reads. */
export type RegionStat = 'mean' | 'max' | 'min'

function statOf(r: RegionFrameResult | null, stat: RegionStat): number | null {
  if (!r || r.cellsWithData === 0) return null
  return stat === 'mean' ? r.mean : stat === 'max' ? r.max : r.min
}

/**
 * The series as the pins' graph draws one (`measure/chartGeometry.ts`): one
 * slot per frame up to `MAX_CHART_SLOTS`; past that a slot shows the last
 * frame (in time) read into it.
 */
export function regionChartSeries(series: RegionSeries, stat: RegionStat): MeasurementSeries | null {
  const n = series.results.length
  const chart = createSeries(series.axis.startMs, frameStampMs(series.axis, n - 1), Math.min(n, MAX_CHART_SLOTS))
  if (!chart) return null
  const slots = chart.slots.length
  for (let i = 0; i < n; i++) {
    const r = series.results[i]
    if (!r) continue
    const slot = n <= slots ? i : Math.min(slots - 1, Math.round((i / (n - 1)) * (slots - 1)))
    chart.slots[slot] = { sampled: true, value: statOf(r, stat), timeMs: frameStampMs(series.axis, i) }
  }
  return chart
}

/** One record of the series: the frame, its value, the frames tied with it, and where. */
export interface RegionRecord {
  readonly frame: number
  readonly timeMs: number
  readonly value: number
  /** Other frames holding exactly this value. */
  readonly others: number
  /** For the record high and low: the first cell holding it, and how many cells did. */
  readonly at?: { lat: number; lon: number } | null
  readonly cells?: number
}

export interface RegionRecords {
  /** The highest value in any cell of any frame read, and the lowest. */
  readonly high: RegionRecord
  readonly low: RegionRecord
  /** The frames whose region mean was highest and lowest. */
  readonly highMean: RegionRecord | null
  readonly lowMean: RegionRecord | null
}

/** The records over the frames read; null before any frame with data. */
export function regionRecords(series: RegionSeries): RegionRecords | null {
  const res = series.results
  const n = res.length
  const highs = extremesOf(n, (i) => statOf(res[i], 'max'))
  const lows = extremesOf(n, (i) => statOf(res[i], 'min'))
  if (!highs || !lows) return null
  const means = extremesOf(n, (i) => statOf(res[i], 'mean'))
  const rec = (frame: number, value: number, ties: readonly number[], place?: 'max' | 'min'): RegionRecord => {
    const r = res[frame]!
    return {
      frame,
      timeMs: frameStampMs(series.axis, frame),
      value,
      others: ties.length - 1,
      ...(place === 'max' ? { at: r.maxAt, cells: r.maxCells } : place === 'min' ? { at: r.minAt, cells: r.minCells } : {}),
    }
  }
  return {
    high: rec(highs.max.index, highs.max.value, highs.max.ties, 'max'),
    low: rec(lows.min.index, lows.min.value, lows.min.ties, 'min'),
    highMean: means ? rec(means.max.index, means.max.value, means.max.ties) : null,
    lowMean: means ? rec(means.min.index, means.min.value, means.min.ties) : null,
  }
}

// ---------------------------------------------------------------- the CSV

/** The method, for the CSV and the methods text. */
export const REGION_SERIES_METHOD =
  'region through time: per frame, over the region\'s cells of the dataset\'s own grid (no regridding or reprojection; whole cells whose centres fall in the region, and for a country only the cells whose centres fall inside its Natural Earth 1:50m outline), the area-weighted mean (cell area R^2 * dLon * (sin lat2 - sin lat1) on a sphere of radius 6371.0072 km, the WGS84 authalic radius) and the highest and lowest decoded cell values; cells without data are excluded and counted; where = the first cell in the grid\'s reading order holding the value, with the number of cells holding it; record high and low = the highest and lowest cell value over the frames read, the first frame in time named and tied frames counted' // i18n-exempt: CSV row, English like every CSV row

type Cell = string | number | null | undefined

function cell(value: Cell): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function csvRows(list: readonly Cell[][]): string {
  return list.map((r) => r.map(cell).join(',')).join('\r\n')
}

export interface RegionCsvContext {
  readonly datasetTitle: string | null
  readonly units?: string
  readonly sourceUnits?: string
  /** The region as the panel names it (English), and its box when it is one. */
  readonly region: string
  readonly regionBounds?: { n: number; s: number; w: number; e: number } | null
  /** For a country: its outline's source. */
  readonly regionShape?: string | null
  readonly gridWidth: number
  readonly gridHeight: number
  /** One step of the data; null when it is not uniform (log or stepped scales). */
  readonly step: number | null
  /** The provenance rows every AYNI CSV carries (`research/citation.ts`), without one frame's time. */
  readonly provenance?: readonly (readonly Cell[])[] | null
  /** A frame's time at the axis's precision ("1991", "1991-01"); null or absent: the ISO instant. */
  readonly formatTime?: ((ms: number) => string | null) | null
}

/** The series as CSV: what was measured and how, the records, then one row per frame read, in time order. */
export function buildRegionCsvText(series: RegionSeries, ctx: RegionCsvContext): string {
  const time = (ms: number): string => ctx.formatTime?.(ms) ?? new Date(ms).toISOString()
  const first = series.results.find((r) => r !== null) ?? null
  const b = ctx.regionBounds
  const records = regionRecords(series)
  const recRow = (name: string, r: RegionRecord | null): Cell[] => (r
    ? [name, r.value, time(r.timeMs), r.frame, r.others, r.at?.lat ?? '', r.at?.lon ?? '', r.cells ?? '']
    : [name])
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['analysis', 'region through time'],
    ['units', ctx.units ?? ''],
    ...(ctx.sourceUnits ? [['source_units', ctx.sourceUnits] as Cell[]] : []),
    ['quantisation_step', ctx.step ?? 'uneven (log or stepped scale): one step at each value'],
    ['region', ctx.region],
    ...(b ? [['region_bounds_nswe', b.n, b.s, b.w, b.e] as Cell[]] : []),
    ...(ctx.regionShape ? [['region_shape', ctx.regionShape] as Cell[]] : []),
    ['region_cells', first?.cellsExamined ?? ''],
    ['region_area_km2', first?.regionAreaKm2 ?? ''],
    ['grid', `${ctx.gridWidth} x ${ctx.gridHeight} cells (the dataset's own)`],
    ['earth_radius_km', `${AUTHALIC_RADIUS_KM} (WGS84 authalic)`],
    ['frames_in_axis', series.results.length],
    ['frames_not_measured', skippedThresholdFrames(series), "filled, interpolated, estimated or missing in the publisher's .dsa availability; not read"],
    ['frames_read', series.read],
    ['time_axis', 'UTC'],
    ['method', REGION_SERIES_METHOD],
    ...(ctx.provenance ?? []).map((r) => [...r]),
    [],
    ['record', 'value', 'frame_time', 'frame_index', 'other_frames_with_the_same_value', 'lat', 'lon', 'cells_with_the_value'],
    recRow('highest_value', records?.high ?? null),
    recRow('lowest_value', records?.low ?? null),
    recRow('highest_mean', records?.highMean ?? null),
    recRow('lowest_mean', records?.lowMean ?? null),
    [],
    ['frame_time', 'mean', 'min', 'min_lat', 'min_lon', 'min_cells', 'max', 'max_lat', 'max_lon', 'max_cells', 'cells_with_data', 'cells_without_data', 'area_with_data_km2', 'frame_index'],
  ]
  const body: Cell[][] = []
  series.results.forEach((r, i) => {
    if (!r) return
    body.push([
      time(frameStampMs(series.axis, i)),
      r.mean,
      r.min,
      r.minAt?.lat,
      r.minAt?.lon,
      r.cellsWithData ? r.minCells : null,
      r.max,
      r.maxAt?.lat,
      r.maxAt?.lon,
      r.cellsWithData ? r.maxCells : null,
      r.cellsWithData,
      r.cellsExamined - r.cellsWithData,
      r.areaWithDataKm2,
      i,
    ])
  })
  return `${csvRows(head)}\r\n${csvRows(body)}\r\n`
}
